import {
  Button,
  Group,
  Icon,
  IconButton,
  Stack,
  Text,
  Title,
} from "@assetwatch/ui";
import type { Dispatch, SetStateAction } from "react";
import { ProcedureOutlineItem } from "./ProcedureOutlineItem";
import {
  duplicateGroup,
  duplicateSection,
  moveItemToSectionBefore,
  type ProcedureAuthoringDraft,
  type ProcedureGroup,
  type ProcedureItem,
  type ProcedureSection,
  reorderGroups,
  reorderItems,
  reorderSections,
} from "./procedureAuthoring";

type SelectedStructure = { kind: "heading" | "section"; id: string } | null;

type ItemListProps = {
  errors: Record<string, string>;
  groupId: string | null;
  items: ProcedureItem[];
  sectionId: string | null;
  selectedItemId: string | null;
  setDraggingItem: Dispatch<
    SetStateAction<{ sectionId: string | null; itemId: string } | null>
  >;
  setDraft: Dispatch<SetStateAction<ProcedureAuthoringDraft>>;
  setSelectedItemId: Dispatch<SetStateAction<string | null>>;
  addItemAfter: (
    groupId: string | null,
    sectionId: string | null,
    afterItemId: string,
  ) => void;
  duplicateItemAndFocus: (sectionId: string | null, itemId: string) => void;
  handleItemDragOver: (sectionId: string | null, targetItemId: string) => void;
  handleItemDrop: (sectionId: string | null, targetItemId?: string) => void;
  removeItem: (sectionId: string | null, itemId: string) => void;
  addNewItem: (groupId: string | null, sectionId: string | null) => void;
  sectionOptions: { value: string; label: string }[];
};

export function ProcedureItemList({
  errors,
  groupId,
  items,
  sectionId,
  selectedItemId,
  setDraggingItem,
  setDraft,
  setSelectedItemId,
  addItemAfter,
  duplicateItemAndFocus,
  handleItemDragOver,
  handleItemDrop,
  removeItem,
  addNewItem,
  sectionOptions,
}: ItemListProps) {
  return (
    <Stack gap={8}>
      {sectionId === null && items.length === 0 ? (
        <section
          aria-label="Unsectioned step drop target"
          className="flex min-h-(--spacing-40) items-center justify-center rounded-(--radius-sm) border border-dashed border-outline-subtle text-(length:--font-size-label-small) text-text-subtle"
          data-testid="unsectioned-drop-target"
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            handleItemDrop(null);
          }}
        >
          Drop steps here
        </section>
      ) : null}
      {items.map((item) => (
        <ProcedureOutlineItem
          item={item}
          key={item.id}
          errorKey={errors[item.id] ? item.id : undefined}
          onDelete={() => removeItem(sectionId, item.id)}
          onAddAfter={() => addItemAfter(groupId, sectionId, item.id)}
          onDragStart={() => setDraggingItem({ sectionId, itemId: item.id })}
          onDragOver={() => handleItemDragOver(sectionId, item.id)}
          onDrop={() => handleItemDrop(sectionId, item.id)}
          onDragEnd={() => setDraggingItem(null)}
          onDuplicate={() => duplicateItemAndFocus(sectionId, item.id)}
          onMove={(direction) =>
            setDraft((current) =>
              reorderItems(current, sectionId, item.id, direction),
            )
          }
          onMoveToSection={(targetSectionId) =>
            setDraft((current) =>
              moveItemToSectionBefore(
                current,
                sectionId,
                targetSectionId === "__unsectioned__" ? null : targetSectionId,
                item.id,
              ),
            )
          }
          onSelect={() => setSelectedItemId(item.id)}
          sectionId={sectionId}
          sectionOptions={sectionOptions}
          selected={selectedItemId === item.id}
        />
      ))}
      <div className="self-start">
        <Button
          icon="add-plus"
          onClick={() => addNewItem(groupId, sectionId)}
          size="sm"
          variant="ghost"
        >
          Add step
        </Button>
      </div>
    </Stack>
  );
}

type SectionBlockProps = {
  errors: Record<string, string>;
  nextId: () => string;
  section: ProcedureSection;
  sectionIndex: number;
  sectionCount: number;
  groupId: string | null;
  selectedItemId: string | null;
  selectedStructure: SelectedStructure;
  setDraft: Dispatch<SetStateAction<ProcedureAuthoringDraft>>;
  setDraggingItem: ItemListProps["setDraggingItem"];
  setSelectedItemId: Dispatch<SetStateAction<string | null>>;
  setSelectedStructure: Dispatch<SetStateAction<SelectedStructure>>;
  addItemAfter: ItemListProps["addItemAfter"];
  addNewItem: ItemListProps["addNewItem"];
  duplicateItemAndFocus: ItemListProps["duplicateItemAndFocus"];
  handleItemDragOver: ItemListProps["handleItemDragOver"];
  handleItemDrop: ItemListProps["handleItemDrop"];
  removeItem: ItemListProps["removeItem"];
  removeSection: (groupId: string | null, sectionId: string) => void;
  sectionOptions: ItemListProps["sectionOptions"];
};

export function ProcedureSectionBlock({
  errors,
  nextId,
  section,
  sectionIndex,
  sectionCount,
  groupId,
  selectedItemId,
  selectedStructure,
  setDraft,
  setDraggingItem,
  setSelectedItemId,
  setSelectedStructure,
  addItemAfter,
  addNewItem,
  duplicateItemAndFocus,
  handleItemDragOver,
  handleItemDrop,
  removeItem,
  removeSection,
  sectionOptions,
}: SectionBlockProps) {
  const selectSection = () => {
    setSelectedItemId(null);
    setSelectedStructure({ kind: "section", id: section.id });
  };

  return (
    <Stack
      gap={8}
      data-testid="procedure-section-block"
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        handleItemDrop(section.id);
      }}
    >
      <Group gap={8} onClick={selectSection}>
        <Icon aria-hidden="true" name="grip-drag" size="xs" />
        <input
          aria-label="Section title"
          data-procedure-error={`section:${section.id}`}
          id={`procedure-section-${section.id}`}
          className="min-w-0 flex-1 border-0 bg-transparent px-(--spacing-8) py-(--spacing-4) text-(length:--font-size-label-small) font-(number:--font-weight-semibold) uppercase tracking-wide text-text-subtle outline-none placeholder:text-text-subtle focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-button-focus-ring"
          onChange={(event) =>
            setDraft((current) => {
              if (!groupId) {
                return {
                  ...current,
                  looseSections: current.looseSections.map((candidate) =>
                    candidate.id === section.id
                      ? { ...candidate, title: event.target.value }
                      : candidate,
                  ),
                };
              }
              return {
                ...current,
                groups: current.groups.map((group) =>
                  group.id === groupId
                    ? {
                        ...group,
                        sections: group.sections.map((candidate) =>
                          candidate.id === section.id
                            ? { ...candidate, title: event.target.value }
                            : candidate,
                        ),
                      }
                    : group,
                ),
              };
            })
          }
          onFocus={selectSection}
          placeholder="Section name"
          value={section.title}
        />
        {selectedStructure?.kind === "section" &&
        selectedStructure.id === section.id ? (
          <>
            <IconButton
              aria-label="Move section up"
              icon="chevron-up"
              disabled={sectionIndex === 0}
              onClick={() =>
                setDraft((current) =>
                  reorderSections(current, groupId, section.id, -1),
                )
              }
              size="sm"
              variant="ghost"
            />
            <IconButton
              aria-label="Move section down"
              icon="chevron-down"
              disabled={sectionIndex === sectionCount - 1}
              onClick={() =>
                setDraft((current) =>
                  reorderSections(current, groupId, section.id, 1),
                )
              }
              size="sm"
              variant="ghost"
            />
            <IconButton
              aria-label="Delete section"
              disabled={Boolean(groupId && sectionCount === 1)}
              icon="trash-delete-bin"
              intent="destructive"
              onClick={() => removeSection(groupId, section.id)}
              size="sm"
              variant="ghost"
            />
            <IconButton
              aria-label="Duplicate section"
              icon="copy-regular"
              onClick={() =>
                setDraft((current) =>
                  duplicateSection(current, groupId, section.id, nextId),
                )
              }
              size="sm"
              variant="ghost"
            />
          </>
        ) : null}
      </Group>
      <ProcedureItemList
        addItemAfter={addItemAfter}
        addNewItem={addNewItem}
        duplicateItemAndFocus={duplicateItemAndFocus}
        errors={errors}
        groupId={groupId}
        handleItemDragOver={handleItemDragOver}
        handleItemDrop={handleItemDrop}
        items={section.items}
        removeItem={removeItem}
        sectionId={section.id}
        sectionOptions={sectionOptions}
        selectedItemId={selectedItemId}
        setDraft={setDraft}
        setDraggingItem={setDraggingItem}
        setSelectedItemId={setSelectedItemId}
      />
    </Stack>
  );
}

type HeadingBlockProps = Omit<
  SectionBlockProps,
  "section" | "sectionIndex" | "sectionCount" | "groupId"
> & {
  group: ProcedureGroup;
  groupIndex: number;
  groupCount: number;
  collapsed: boolean;
  setCollapsedGroups: Dispatch<SetStateAction<Record<string, boolean>>>;
  removeGroup: (groupId: string) => void;
  addSectionToGroup: (groupId: string | null) => void;
};

export function ProcedureHeadingBlock({
  errors,
  nextId,
  group,
  groupIndex,
  groupCount,
  collapsed,
  selectedItemId,
  selectedStructure,
  setCollapsedGroups,
  setDraft,
  setDraggingItem,
  setSelectedItemId,
  setSelectedStructure,
  addItemAfter,
  addNewItem,
  addSectionToGroup,
  duplicateItemAndFocus,
  handleItemDragOver,
  handleItemDrop,
  removeItem,
  removeGroup,
  removeSection,
  sectionOptions,
}: HeadingBlockProps) {
  return (
    <div className="overflow-hidden rounded-(--radius-sm) border border-outline-subtle">
      <div className="px-(--spacing-12) py-(--spacing-10)">
        <Group
          gap={8}
          onClick={() => {
            setSelectedItemId(null);
            setSelectedStructure({ kind: "heading", id: group.id });
          }}
        >
          <IconButton
            aria-label={collapsed ? "Expand heading" : "Collapse heading"}
            onClick={(event) => {
              event.stopPropagation();
              setCollapsedGroups((current) => ({
                ...current,
                [group.id]: !current[group.id],
              }));
            }}
            icon={collapsed ? "chevron-right" : "chevron-down"}
            size="sm"
            variant="ghost"
          />
          <Icon aria-hidden="true" name="grip-drag" size="xs" />
          <input
            aria-label="Heading title"
            data-procedure-error={`heading:${group.id}`}
            id={`procedure-heading-${group.id}`}
            className="min-w-0 flex-1 border-0 bg-transparent px-(--spacing-8) py-(--spacing-4) font-(number:--font-weight-bold) text-text-default outline-none placeholder:text-text-subtle focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-button-focus-ring"
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                groups: current.groups.map((candidate) =>
                  candidate.id === group.id
                    ? { ...candidate, title: event.target.value }
                    : candidate,
                ),
              }))
            }
            onFocus={() => {
              setSelectedItemId(null);
              setSelectedStructure({ kind: "heading", id: group.id });
            }}
            placeholder="Heading name"
            value={group.title}
          />
          <Text tone="subtle">
            {group.sections.reduce(
              (count, section) => count + section.items.length,
              0,
            )}{" "}
            steps
          </Text>
          {selectedStructure?.kind === "heading" &&
          selectedStructure.id === group.id ? (
            <>
              <IconButton
                aria-label="Move heading up"
                icon="chevron-up"
                disabled={groupIndex === 0}
                onClick={() =>
                  setDraft((current) => reorderGroups(current, group.id, -1))
                }
                size="sm"
                variant="ghost"
              />
              <IconButton
                aria-label="Move heading down"
                icon="chevron-down"
                disabled={groupIndex === groupCount - 1}
                onClick={() =>
                  setDraft((current) => reorderGroups(current, group.id, 1))
                }
                size="sm"
                variant="ghost"
              />
              <IconButton
                aria-label="Delete heading"
                icon="trash-delete-bin"
                intent="destructive"
                onClick={() => removeGroup(group.id)}
                size="sm"
                variant="ghost"
              />
              <IconButton
                aria-label="Duplicate heading"
                icon="copy-regular"
                onClick={() =>
                  setDraft((current) =>
                    duplicateGroup(current, group.id, nextId),
                  )
                }
                size="sm"
                variant="ghost"
              />
            </>
          ) : null}
        </Group>
      </div>
      {!collapsed ? (
        <div className="px-(--spacing-12) pb-(--spacing-12) pt-(--spacing-8)">
          <Stack gap={8}>
            {group.sections.map((section, sectionIndex) => (
              <ProcedureSectionBlock
                addItemAfter={addItemAfter}
                addNewItem={addNewItem}
                duplicateItemAndFocus={duplicateItemAndFocus}
                errors={errors}
                groupId={group.id}
                handleItemDragOver={handleItemDragOver}
                handleItemDrop={handleItemDrop}
                key={section.id}
                nextId={nextId}
                removeItem={removeItem}
                removeSection={removeSection}
                sectionOptions={sectionOptions}
                section={section}
                sectionCount={group.sections.length}
                sectionIndex={sectionIndex}
                selectedItemId={selectedItemId}
                selectedStructure={selectedStructure}
                setDraft={setDraft}
                setDraggingItem={setDraggingItem}
                setSelectedItemId={setSelectedItemId}
                setSelectedStructure={setSelectedStructure}
              />
            ))}
            <div className="pt-(--spacing-8)">
              <Button
                icon="add-plus"
                onClick={() => addSectionToGroup(group.id)}
                size="sm"
                variant="ghost"
              >
                Add section
              </Button>
            </div>
          </Stack>
        </div>
      ) : null}
    </div>
  );
}

export function ProcedureStepsHeader({
  draft,
  addHeading,
  addSectionToGroup,
}: {
  draft: ProcedureAuthoringDraft;
  addHeading: () => void;
  addSectionToGroup: (groupId: string | null) => void;
}) {
  return (
    <div className="flex items-center gap-(--spacing-12) border-b border-outline-subtle bg-surface-container-low px-(--spacing-16) py-(--spacing-12)">
      <Title as="h2" size="lg">
        Steps
      </Title>
      <span className="text-(length:--font-size-label-small) uppercase tracking-wide text-text-subtle">
        {draft.looseItems.length +
          draft.looseSections.reduce(
            (count, section) => count + section.items.length,
            0,
          ) +
          draft.groups.reduce(
            (count, group) =>
              count +
              group.sections.reduce(
                (sectionCount, section) => sectionCount + section.items.length,
                0,
              ),
            0,
          )}{" "}
        steps
      </span>
      <div className="ml-auto">
        <Group gap={8}>
          <Button
            icon="add-plus"
            onClick={() => addSectionToGroup(null)}
            size="sm"
            variant="ghost"
          >
            Add section
          </Button>
          <Button
            icon="add-plus"
            onClick={addHeading}
            size="sm"
            variant="outline"
          >
            Add heading
          </Button>
        </Group>
      </div>
    </div>
  );
}
